// pages/EnrichissementCompletPage.jsx
// Page dédiée pour l'enrichissement complet IA des mots

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Sparkles, Loader2, RefreshCw, Languages, AlertTriangle, Eye, EyeOff, StopCircle } from 'lucide-react';
import './EnrichissementCompletPage.css';

export default function EnrichissementCompletPage() {
  const [mots, setMots] = useState([]);
  const [totalMots, setTotalMots] = useState(0); // Total de mots pour la langue
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [filterLangue, setFilterLangue] = useState('fr');
  const [filterType, setFilterType] = useState('tous'); // Nouveau filtre par type

  // Affichage liste complète des mots
  const [showAllMots, setShowAllMots] = useState(false);
  const [allMotsList, setAllMotsList] = useState([]);
  const [loadingAllMots, setLoadingAllMots] = useState(false);

  // Enrichissement IA complet
  const [enriching, setEnriching] = useState(false);
  const [enrichBatchSize, setEnrichBatchSize] = useState('10');
  const [stopRequested, setStopRequested] = useState(false);
  const shouldStopRef = useRef(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [resultatsEnCours, setResultatsEnCours] = useState({ enriched: 0, translated: 0, transitive: 0, transitivesChecked: 0, errors: [] });
  const [motsTraitesEnDirect, setMotsTraitesEnDirect] = useState([]);
  const [resultatFinal, setResultatFinal] = useState(null); // modale finale

  // Options d'enrichissement (checkboxes) - servent aussi de filtres
  const [enrichOptions, setEnrichOptions] = useState({
    francais: false,
    anglais: false,
    espagnol: false,
    pluriel: false,
    genre: false,
    transitives: false,
  });

  const langues = [
    { code: 'fr', label: 'Francais', flag: '🇫🇷' },
    { code: 'es', label: 'Espagnol', flag: '🇪🇸' },
    { code: 'en', label: 'Anglais', flag: '🇬🇧' },
  ];

  const typesMots = [
    { code: 'tous', label: 'Tous les types' },
    { code: 'nom', label: 'Noms' },
    { code: 'adjectif', label: 'Adjectifs' },
    { code: 'adverbe', label: 'Adverbes' },
    { code: 'pronom', label: 'Pronoms' },
    { code: 'verbe', label: 'Verbes' },
    { code: 'expression', label: 'Expressions' },
  ];

  // Charger le total de mots pour la langue (et type si filtré)
  const fetchTotalMots = useCallback(async () => {
    try {
      let url = `/api/mots?langue=${filterLangue}&limit=10000`;
      if (filterType !== 'tous') {
        url += `&type=${filterType}`;
      }
      const response = await fetch(url);
      const data = await response.json();
      if (data.success) {
        setTotalMots(data.count);
      }
    } catch (err) {
      console.error('Erreur chargement total:', err);
    }
  }, [filterLangue, filterType]);

  // Charger tous les mots pour affichage (bouton "Voir les mots")
  const fetchAllMots = useCallback(async () => {
    setLoadingAllMots(true);
    try {
      let url = `/api/mots?langue=${filterLangue}&limit=10000`;
      if (filterType !== 'tous') {
        url += `&type=${filterType}`;
      }
      const response = await fetch(url);
      const data = await response.json();
      if (data.success) {
        setAllMotsList(data.mots);
      }
    } catch (err) {
      console.error('Erreur chargement mots:', err);
    } finally {
      setLoadingAllMots(false);
    }
  }, [filterLangue, filterType]);

  // Toggle affichage liste des mots
  const toggleShowAllMots = () => {
    if (!showAllMots) {
      fetchAllMots();
    }
    setShowAllMots(!showAllMots);
  };

  // Charger les mots selon les checkboxes cochées (logique OR)
  const fetchMots = useCallback(async () => {
    // Vérifier si au moins une option est cochée
    const hasAnyOption = enrichOptions.francais || enrichOptions.anglais ||
                         enrichOptions.espagnol || enrichOptions.pluriel || enrichOptions.genre ||
                         enrichOptions.transitives;

    if (!hasAnyOption) {
      setMots([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    setSelectedIds([]);

    try {
      const allMots = new Map(); // Utiliser Map pour éviter les doublons (clé = id)

      // Fetch mots sans traduction française (si coché et langue source != fr)
      if (enrichOptions.francais && filterLangue !== 'fr') {
        const response = await fetch(`/api/mots/traductions-incompletes?langueSource=${filterLangue}&langueCible=fr`);
        const data = await response.json();
        if (data.success) {
          data.mots.forEach(m => {
            if (!allMots.has(m.id)) {
              allMots.set(m.id, {
                id: m.id,
                motSingulier: m.motSingulier,
                langue: m.langue,
                type: m.type,
                genre: m.genre,
                motPluriel: m.motPluriel,
                manque: ['🇫🇷'],
              });
            } else {
              allMots.get(m.id).manque.push('🇫🇷');
            }
          });
        }
      }

      // Fetch mots sans traduction anglaise (si coché et langue source != en)
      if (enrichOptions.anglais && filterLangue !== 'en') {
        const response = await fetch(`/api/mots/traductions-incompletes?langueSource=${filterLangue}&langueCible=en`);
        const data = await response.json();
        if (data.success) {
          data.mots.forEach(m => {
            if (!allMots.has(m.id)) {
              allMots.set(m.id, {
                id: m.id,
                motSingulier: m.motSingulier,
                langue: m.langue,
                type: m.type,
                genre: m.genre,
                motPluriel: m.motPluriel,
                manque: ['🇬🇧'],
              });
            } else {
              allMots.get(m.id).manque.push('🇬🇧');
            }
          });
        }
      }

      // Fetch mots sans traduction espagnole (si coché et langue source != es)
      if (enrichOptions.espagnol && filterLangue !== 'es') {
        const response = await fetch(`/api/mots/traductions-incompletes?langueSource=${filterLangue}&langueCible=es`);
        const data = await response.json();
        if (data.success) {
          data.mots.forEach(m => {
            if (!allMots.has(m.id)) {
              allMots.set(m.id, {
                id: m.id,
                motSingulier: m.motSingulier,
                langue: m.langue,
                type: m.type,
                genre: m.genre,
                motPluriel: m.motPluriel,
                manque: ['🇪🇸'],
              });
            } else {
              allMots.get(m.id).manque.push('🇪🇸');
            }
          });
        }
      }

      // Fetch mots sans pluriel (si coché)
      if (enrichOptions.pluriel) {
        const response = await fetch(`/api/mots/incomplets?langue=${filterLangue}&typePluriel=pluriel-omis`);
        const data = await response.json();
        if (data.success) {
          data.mots.forEach(m => {
            if (!allMots.has(m.id)) {
              allMots.set(m.id, {
                id: m.id,
                motSingulier: m.motSingulier,
                langue: m.langue,
                type: m.type,
                genre: m.genre,
                motPluriel: m.motPluriel,
                manque: ['📝'],
              });
            } else {
              allMots.get(m.id).manque.push('📝');
            }
          });
        }
      }

      // Fetch mots sans genre (si coché)
      if (enrichOptions.genre) {
        const response = await fetch(`/api/mots/incomplets?langue=${filterLangue}&typeGenre=sans-genre`);
        const data = await response.json();
        if (data.success) {
          data.mots.forEach(m => {
            if (!allMots.has(m.id)) {
              allMots.set(m.id, {
                id: m.id,
                motSingulier: m.motSingulier,
                langue: m.langue,
                type: m.type,
                genre: m.genre,
                motPluriel: m.motPluriel,
                manque: ['👤'],
              });
            } else {
              allMots.get(m.id).manque.push('👤');
            }
          });
        }
      }

      // Fetch mots avec traductions transitives manquantes (si coché)
      if (enrichOptions.transitives) {
        const response = await fetch(`/api/mots/transitives-manquantes?langue=${filterLangue}`);
        const data = await response.json();
        if (data.success) {
          data.mots.forEach(m => {
            if (!allMots.has(m.id)) {
              allMots.set(m.id, {
                id: m.id,
                motSingulier: m.motSingulier,
                langue: m.langue,
                type: m.type,
                genre: m.genre,
                motPluriel: m.motPluriel,
                manque: ['🔗'],
                transitivesMissing: m.transitivesMissing,
              });
            } else {
              allMots.get(m.id).manque.push('🔗');
              allMots.get(m.id).transitivesMissing = m.transitivesMissing;
            }
          });
        }
      }

      // Convertir Map en Array
      let motsArray = Array.from(allMots.values());

      // Filtrer par type si nécessaire
      if (filterType !== 'tous') {
        motsArray = motsArray.filter(m => m.type === filterType);
      }

      // Trier alphabétiquement
      motsArray.sort((a, b) => a.motSingulier.localeCompare(b.motSingulier));

      setMots(motsArray);
    } catch (err) {
      setError(err.message);
      setMots([]);
    } finally {
      setLoading(false);
    }
  }, [filterLangue, filterType, enrichOptions]);

  // Charger le total quand la langue ou le type change
  useEffect(() => {
    fetchTotalMots();
    // Reset les checkboxes quand la langue change (pas le type)
  }, [filterLangue, filterType, fetchTotalMots]);

  // Reset les checkboxes quand la langue change
  useEffect(() => {
    setEnrichOptions({
      francais: false,
      anglais: false,
      espagnol: false,
      pluriel: false,
      genre: false,
      transitives: false,
    });
    setMots([]);
  }, [filterLangue, fetchTotalMots]);

  // Recharger les mots quand les checkboxes changent
  useEffect(() => {
    fetchMots();
  }, [enrichOptions, fetchMots]);

  // Fermer la liste des mots quand les filtres changent
  useEffect(() => {
    setShowAllMots(false);
    setAllMotsList([]);
  }, [filterLangue, filterType]);

  // Sélection
  const toggleSelection = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === mots.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(mots.map(m => m.id));
    }
  };

  // Arrêter l'enrichissement
  const arreterEnrichissement = () => {
    shouldStopRef.current = true;
    setStopRequested(true);
  };

  // Enrichissement complet IA (par paquets avec possibilité d'arrêt)
  const handleEnrichComplete = async () => {
    const idsToProcess = selectedIds.length > 0 ? selectedIds : mots.map(m => m.id);

    if (idsToProcess.length === 0) {
      alert('Aucun mot a enrichir');
      return;
    }

    // Limiter selon le batch size
    const limit = enrichBatchSize === 'tout' ? idsToProcess.length : parseInt(enrichBatchSize);
    const ids = idsToProcess.slice(0, limit);

    // Construire la liste des langues cibles
    const languesCibles = [];
    if (enrichOptions.francais && filterLangue !== 'fr') languesCibles.push('fr');
    if (enrichOptions.anglais && filterLangue !== 'en') languesCibles.push('en');
    if (enrichOptions.espagnol && filterLangue !== 'es') languesCibles.push('es');

    // Construire le message de confirmation
    const actions = [];
    if (enrichOptions.genre) actions.push('👤 Completer le genre');
    if (enrichOptions.pluriel) actions.push('📝 Completer le pluriel');
    if (languesCibles.length > 0) {
      const langueLabels = { fr: 'francais', es: 'espagnol', en: 'anglais' };
      const languesStr = languesCibles.map(l => langueLabels[l]).join(', ');
      actions.push(`🌍 Traduire vers: ${languesStr}`);
      if (languesCibles.length >= 2) {
        actions.push('🔗 Creer traductions transitives (nouvelles)');
      }
    }
    if (enrichOptions.transitives) {
      actions.push('🔗 Verifier/creer traductions transitives (existantes)');
    }

    if (!window.confirm(
      `Enrichissement de ${ids.length} mot(s) par paquets de 10:\n\n` +
      actions.map((a, i) => `${i + 1}. ${a}`).join('\n') +
      `\n\nCette operation utilise l'API Claude et peut prendre quelques instants.\nVous pourrez l'interrompre a tout moment.`
    )) return;

    setEnriching(true);
    shouldStopRef.current = false;
    setStopRequested(false);
    setResultatsEnCours({ enriched: 0, translated: 0, transitive: 0, transitivesChecked: 0, errors: [] });
    setMotsTraitesEnDirect([]);

    // Découper en paquets de 10
    const taillePaquet = 10;
    const paquets = [];
    for (let i = 0; i < ids.length; i += taillePaquet) {
      paquets.push(ids.slice(i, i + taillePaquet));
    }

    setProgress({ current: 0, total: ids.length });

    const totalResultats = { enriched: 0, translated: 0, transitive: 0, transitivesChecked: 0, errors: [] };

    for (let p = 0; p < paquets.length; p++) {
      // Vérifier si arrêt demandé
      if (shouldStopRef.current) {
        console.log("Enrichissement arrêté par l'utilisateur");
        break;
      }

      const paquet = paquets[p];

      try {
        const response = await fetch('/api/enrichment/enrich-conditionnel', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ids: paquet,
            langueSource: filterLangue,
            languesCibles,
            options: {
              pluriel: enrichOptions.pluriel,
              genre: enrichOptions.genre,
              transitives: enrichOptions.transitives,
            },
          }),
        });

        const data = await response.json();

        if (data.success) {
          const r = data.results;
          totalResultats.enriched += r.enriched || 0;
          totalResultats.translated += r.translated || 0;
          totalResultats.transitive += r.transitive || 0;
          totalResultats.transitivesChecked += r.transitivesChecked || 0;
          if (r.errors?.length > 0) totalResultats.errors.push(...r.errors);
          // Ajouter les mots traités en direct
          if (r.motsTraites) {
            setMotsTraitesEnDirect(prev => [...prev, ...r.motsTraites]);
          }
        } else {
          totalResultats.errors.push({ error: data.error, paquet: p + 1 });
        }
      } catch (err) {
        totalResultats.errors.push({ error: err.message, paquet: p + 1 });
      }

      // Mettre à jour la progression et les résultats en direct
      const motTraites = Math.min((p + 1) * taillePaquet, ids.length);
      setProgress({ current: motTraites, total: ids.length });
      setResultatsEnCours({ ...totalResultats });

      // Petite pause entre les paquets
      if (p < paquets.length - 1 && !shouldStopRef.current) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }

    shouldStopRef.current = false;
    setStopRequested(false);

    // Attendre 2s pour laisser voir les derniers mots traités
    await new Promise(resolve => setTimeout(resolve, 2000));

    setEnriching(false);

    // Afficher la modale de résultat
    setResultatFinal(totalResultats);
  };

  // Fermer la modale et recharger la liste
  const fermerResultatFinal = () => {
    setResultatFinal(null);
    setSelectedIds([]);
    setMotsTraitesEnDirect([]);
    setResultatsEnCours({ enriched: 0, translated: 0, transitive: 0, transitivesChecked: 0, errors: [] });
    fetchMots();
    fetchTotalMots();
  };

  const getLangueFlag = (code) => {
    const langue = langues.find(l => l.code === code);
    return langue ? langue.flag : code;
  };

  // Toggle une option d'enrichissement
  const toggleEnrichOption = (option) => {
    setEnrichOptions(prev => ({
      ...prev,
      [option]: !prev[option],
    }));
  };

  // Vérifier si au moins une option est cochée
  const hasAnyOption = enrichOptions.francais || enrichOptions.anglais ||
                       enrichOptions.espagnol || enrichOptions.pluriel || enrichOptions.genre ||
                       enrichOptions.transitives;

  return (
    <div className="enrichissement-complet-page">
      <div className="page-header">
        <h1>
          <Sparkles size={28} />
          Enrichissement complet IA
        </h1>
        <button className="btn-refresh" onClick={() => { fetchMots(); fetchTotalMots(); }} disabled={loading}>
          <RefreshCw size={18} className={loading ? 'spinning' : ''} />
          Actualiser
        </button>
      </div>

      <div className="description-box">
        <p>
          Cochez les options pour afficher les mots incomplets correspondants, puis lancez l'enrichissement IA.
        </p>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Bandeau 1: Langue + compteurs */}
      <div className="filters-bar">
        <div className="filter-group">
          <Languages size={18} />
          <label>
            Langue source:
            <select
              value={filterLangue}
              onChange={(e) => setFilterLangue(e.target.value)}
              disabled={enriching}
            >
              {langues.map(l => (
                <option key={l.code} value={l.code}>{l.flag} {l.label}</option>
              ))}
            </select>
          </label>
        </div>

        <div className="filter-group">
          <label>
            Type de mot:
            <select
              value={filterType}
              onChange={(e) => {
                const newType = e.target.value;
                setFilterType(newType);
                if (['verbe', 'adverbe', 'expression'].includes(newType)) {
                  setEnrichOptions(prev => ({ ...prev, pluriel: false, genre: false }));
                }
              }}
              disabled={enriching}
            >
              {typesMots.map(t => (
                <option key={t.code} value={t.code}>{t.label}</option>
              ))}
            </select>
          </label>
        </div>

        <div className="stats-info">
          <span className="count">{totalMots}</span> mot(s) au total
          <span className="separator">|</span>
          <span className="count highlight">{mots.length}</span> a enrichir
          {selectedIds.length > 0 && (
            <>
              <span className="separator">|</span>
              <span className="selected-count">
                {selectedIds.length} selectionne(s)
              </span>
            </>
          )}
        </div>

        <button
          className="btn-voir-mots"
          onClick={toggleShowAllMots}
          disabled={loadingAllMots || totalMots === 0}
        >
          {loadingAllMots ? (
            <Loader2 size={16} className="spinning" />
          ) : showAllMots ? (
            <EyeOff size={16} />
          ) : (
            <Eye size={16} />
          )}
          {showAllMots ? 'Masquer' : 'Voir les mots'}
        </button>
      </div>

      {/* Liste dépliable des mots (sous le premier bandeau) */}
      {showAllMots && (
        <div className="all-mots-panel">
          <div className="all-mots-header">
            <span>Liste des {allMotsList.length} mot(s) {filterType !== 'tous' ? `de type "${filterType}"` : ''} en {langues.find(l => l.code === filterLangue)?.label || filterLangue}</span>
          </div>
          <div className="all-mots-list">
            {allMotsList.map((mot) => (
              <div key={mot.id} className="all-mots-item">
                <span className="mot-singulier">{mot.motSingulier}</span>
                {mot.motPluriel && <span className="mot-pluriel">({mot.motPluriel})</span>}
                <span className="mot-type">{mot.type || '-'}</span>
                {mot.genre && <span className="mot-genre">{mot.genre}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bandeau 2: Checkboxes (filtrent ET définissent l'enrichissement) */}
      <div className="enrichment-bar">
        <div className="enrichment-options">
          <div className="enrich-checkboxes">
            {/* Traductions - masquer la langue source */}
            <div className="checkbox-group traductions">
              <span className="group-label">Sans traduction vers:</span>
              {filterLangue !== 'fr' && (
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={enrichOptions.francais}
                    onChange={() => toggleEnrichOption('francais')}
                    disabled={enriching}
                  />
                  <span>🇫🇷 Francais</span>
                </label>
              )}
              {filterLangue !== 'en' && (
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={enrichOptions.anglais}
                    onChange={() => toggleEnrichOption('anglais')}
                    disabled={enriching}
                  />
                  <span>🇬🇧 Anglais</span>
                </label>
              )}
              {filterLangue !== 'es' && (
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={enrichOptions.espagnol}
                    onChange={() => toggleEnrichOption('espagnol')}
                    disabled={enriching}
                  />
                  <span>🇪🇸 Espagnol</span>
                </label>
              )}
              <label className="checkbox-label" title="Verifier les traductions transitives entre langues (sans API)">
                <input
                  type="checkbox"
                  checked={enrichOptions.transitives}
                  onChange={() => toggleEnrichOption('transitives')}
                  disabled={enriching}
                />
                <span>🔗 Transitives</span>
              </label>
            </div>

            {/* Enrichissement données - masqué pour verbes, adverbes, expressions */}
            {!['verbe', 'adverbe', 'expression'].includes(filterType) && (
            <div className="checkbox-group donnees">
              <span className="group-label">Incomplet:</span>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={enrichOptions.pluriel}
                  onChange={() => toggleEnrichOption('pluriel')}
                  disabled={enriching}
                />
                <span>📝 Pluriel</span>
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={enrichOptions.genre}
                  onChange={() => toggleEnrichOption('genre')}
                  disabled={enriching}
                />
                <span>👤 Genre</span>
              </label>
            </div>
            )}
          </div>

          <div className="enrichment-actions">
            <label className="quantity-label">
              Quantite:
              <select
                value={enrichBatchSize}
                onChange={(e) => setEnrichBatchSize(e.target.value)}
                disabled={enriching}
              >
                <option value="5">5</option>
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
                <option value="tout">Tout ({selectedIds.length > 0 ? selectedIds.length : mots.length})</option>
              </select>
            </label>

            <button
              className="btn-enrich-complete"
              onClick={handleEnrichComplete}
              disabled={enriching || mots.length === 0 || !hasAnyOption}
            >
              {enriching ? (
                <>
                  <Loader2 size={16} className="spinning" />
                  Enrichissement...
                </>
              ) : (
                <>
                  <Sparkles size={16} />
                  Enrichir avec l'IA
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Barre de progression pendant l'enrichissement */}
      {enriching && (
        <div className="progress-container">
          <div className="progress-header">
            <Loader2 size={20} className="spinning" />
            <span>
              Enrichissement en cours... {progress.current} / {progress.total} mot(s)
            </span>
          </div>

          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ width: `${progress.total > 0 ? (progress.current / progress.total) * 100 : 0}%` }}
            ></div>
          </div>

          <div className="progress-stats">
            {resultatsEnCours.enriched > 0 && <span>📝 {resultatsEnCours.enriched} enrichi(s)</span>}
            {resultatsEnCours.translated > 0 && <span>🌍 {resultatsEnCours.translated} traduit(s)</span>}
            {resultatsEnCours.transitive > 0 && <span>🔗 {resultatsEnCours.transitive} transitive(s)</span>}
            {resultatsEnCours.errors.length > 0 && <span>❌ {resultatsEnCours.errors.length} erreur(s)</span>}
          </div>

          <button onClick={arreterEnrichissement} className="btn-stop" disabled={stopRequested}>
            <StopCircle size={18} />
            {stopRequested ? 'Arrêt en cours...' : "Arrêter l'enrichissement"}
          </button>

          {/* Liste des mots traités en direct */}
          {motsTraitesEnDirect.length > 0 && (
            <div className="mots-traites-direct">
              {motsTraitesEnDirect.map((m, i) => (
                <div key={i} className={`mot-traite ${m.statut}`}>
                  <span className="mot-traite-icone">{m.statut === 'ok' ? '✅' : '❌'}</span>
                  <span className="mot-traite-nom">{m.mot}</span>
                  {m.actions.length > 0 && (
                    <span className="mot-traite-actions">{m.actions.join(', ')}</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Modale de résultat final */}
      {resultatFinal && (
        <div className="modale-overlay" onClick={fermerResultatFinal}>
          <div className="modale-resultat" onClick={(e) => e.stopPropagation()}>
            <h3>Enrichissement terminé</h3>
            <div className="modale-stats">
              {resultatFinal.enriched > 0 && <p>📝 {resultatFinal.enriched} mot(s) enrichi(s) (genre/pluriel)</p>}
              {resultatFinal.translated > 0 && <p>🌍 {resultatFinal.translated} traduction(s) créée(s)</p>}
              {resultatFinal.transitive > 0 && <p>🔗 {resultatFinal.transitive} transitive(s) nouvelles</p>}
              {resultatFinal.transitivesChecked > 0 && <p>🔗 {resultatFinal.transitivesChecked} transitive(s) vérifiée(s)</p>}
              {resultatFinal.errors.length > 0 && <p>❌ {resultatFinal.errors.length} erreur(s)</p>}
              {resultatFinal.enriched === 0 && resultatFinal.translated === 0 && resultatFinal.transitive === 0 && resultatFinal.transitivesChecked === 0 && resultatFinal.errors.length === 0 && (
                <p>Aucune modification effectuée</p>
              )}
            </div>
            <button className="btn-modale-ok" onClick={fermerResultatFinal}>OK</button>
          </div>
        </div>
      )}

      {/* Liste des mots (cachée pendant l'enrichissement) */}
      {enriching ? null : loading ? (
        <div className="loading">
          <Loader2 size={24} className="spinning" />
          Chargement...
        </div>
      ) : !hasAnyOption ? (
        <div className="empty-state">
          <AlertTriangle size={48} />
          <p>Cochez au moins une option pour afficher les mots a enrichir</p>
        </div>
      ) : mots.length === 0 ? (
        <div className="empty-state success">
          <Sparkles size={48} />
          <p>Tous les mots sont complets pour les criteres selectionnes !</p>
        </div>
      ) : (
        <div className="mots-table">
          <div className="table-header">
            <div className="col-checkbox">
              <input
                type="checkbox"
                checked={selectedIds.length === mots.length && mots.length > 0}
                onChange={toggleSelectAll}
              />
            </div>
            <div className="col-langue">Langue</div>
            <div className="col-mot">Mot</div>
            <div className="col-type">Type</div>
            <div className="col-genre">Genre</div>
            <div className="col-pluriel">Pluriel</div>
            <div className="col-info">Manque</div>
          </div>

          {mots.map((mot) => (
            <div
              key={mot.id}
              className={`table-row ${selectedIds.includes(mot.id) ? 'selected' : ''}`}
              onClick={() => toggleSelection(mot.id)}
            >
              <div className="col-checkbox" onClick={(e) => e.stopPropagation()}>
                <input
                  type="checkbox"
                  checked={selectedIds.includes(mot.id)}
                  onChange={() => toggleSelection(mot.id)}
                />
              </div>
              <div className="col-langue">{getLangueFlag(mot.langue)}</div>
              <div className="col-mot">{mot.motSingulier}</div>
              <div className="col-type">{mot.type || '-'}</div>
              <div className="col-genre">{mot.genre || <span className="missing">-</span>}</div>
              <div className="col-pluriel">{mot.motPluriel || <span className="missing">-</span>}</div>
              <div className="col-info">
                <span className="badge manque">{mot.manque?.join(' ') || '-'}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
